import { combineReducers } from 'redux';
import { ActionType } from 'typesafe-actions';
import { tipsActions } from './actions';
import {
  PRICING_TIERS_FAILURE,
  PRICING_TIERS_REQUEST,
  PRICING_TIERS_SUCCESS,
  COUPON_REQUEST,
  COUPON_SUCCESS,
  COUPON_FAILURE,
  TIP_PRODUCT_SUCCESS,
  TIP_PRODUCT_REQUEST,
  TIP_PRODUCT_FAILURE,
} from '../../constants/tips';

/* Types */
import {
  FormattedOdds,
  PriceType,
} from '../../components/TipPublish/interfaces/utils';
import { TipType } from '../../components/TipPublish/interfaces/tip/Tip';
import { Price } from '../../components/PaymentGateway/interfaces/Price';
import { TipsterInfo } from '../../components/Shops/shared/interfaces';
import { ConfidenceType } from '../../components/Shops/Tip/components/TipBox/Bottom/Confidence';

export type PricingTierCode =
  | 'com.tipsterchat.tip.tier.005'
  | 'com.tipsterchat.tip.tier.010'
  | 'com.tipsterchat.tip.tier.020';

export interface MatchFromBE {
  id: string;
  sport: string;
  sport_code: string;
  competition_name: string;
  away_player: string;
  country: string;
  home_player: string;
  name: string;
  start_date: string;
}

interface BookieButton {
  background_color: string;
  background_image_url: string;
  bookie_id: string;
  bookie_url: string;
  button_color: string;
  button_text: string;
  button_text_color: string;
  logo_url: string;
  logos: {
    default: string;
    highlighted: string;
  };
  promotion_text: string;
  text_color: string;
  type: string;
}

export type MatchesByID = Record<string, MatchFromBE>;

export type TipResult =
  | 'NOT_STARTED'
  | 'INVALID'
  | 'WON'
  | 'LOST'
  | 'CANCELED'
  | 'VOID'
  | 'HALF_WON'
  | 'HALF_LOST';

export abstract class TipData {
  id: string;
  tipster_id: string;
  type: TipType;
  bookie: string;
  bookie_id: string;
  web_price: Price;
  web_price_with_discount: Price;
  result: TipResult | null;
  rate: number;
  stake: number;
  price_type: PriceType;
  purchased: boolean;
  rating: null;
  sales_are_stopped: boolean;
  final_return: number;
  started_at: string;
  finished_at: string;
  created_at: string;
  updated_at: string;
  edited_at: string;
  only_safe: boolean;
  formatted_odds: FormattedOdds;
  is_argumented: boolean;
  pricing_tier_code: PricingTierCode;
  media_files: IMediaFile[];
  liked: boolean;
  likes_count: number;
  impressions_count: number;
  web_discount_percentage: number;
  bookie_button: BookieButton;
  owned: boolean;
  confidence: ConfidenceType;

  constructor(tip: TipData) {
    this.id = tip.id;
    this.tipster_id = tip.tipster_id;
    this.type = tip.type;
    this.bookie = tip.bookie;
    this.bookie_id = tip.bookie_id;
    this.web_price = tip.web_price;
    this.web_price_with_discount = tip.web_price_with_discount;
    this.rate = tip.rate;
    this.result = tip.result;
    this.stake = tip.stake;
    this.price_type = tip.price_type;
    this.purchased = tip.purchased;
    this.rating = tip.rating;
    this.sales_are_stopped = tip.sales_are_stopped;
    this.final_return = tip.final_return;
    this.started_at = tip.started_at;
    this.finished_at = tip.finished_at;
    this.created_at = tip.created_at;
    this.updated_at = tip.updated_at;
    this.edited_at = tip.edited_at;
    this.only_safe = tip.only_safe;
    this.formatted_odds = tip.formatted_odds;
    this.is_argumented = tip.is_argumented;
    this.pricing_tier_code = tip.pricing_tier_code;
    this.media_files = tip.media_files || [];
    this.liked = tip.liked;
    this.likes_count = tip.likes_count;
    this.impressions_count = tip.impressions_count;
    this.web_discount_percentage = tip.web_discount_percentage;
    this.bookie_button = tip.bookie_button;
    this.owned = tip.owned;
    this.confidence = tip.confidence;
  }
}

export class LockedTipData extends TipData {
  status: string;
  constructor(tip: LockedTipData) {
    super(tip);
    this.status = 'LOCKED';
  }
}

export class UnlockedTipData extends TipData {
  status: string;
  bet_id: string | null;
  match_ids: string[];
  match_forecasts: UnlockedMatchForecast[];
  analysis: string;
  result: TipResult;
  safe_purchased: boolean;
  premium_included: null;
  followed: boolean;
  betslip_image: BetslipImage;

  constructor(tip: UnlockedTipData) {
    super(tip);

    this.bet_id = tip.bet_id;
    this.match_ids = tip.match_ids;
    this.match_forecasts = tip.match_forecasts;
    this.analysis = tip.analysis;
    this.result = tip.result;
    this.safe_purchased = tip.safe_purchased;
    this.premium_included = tip.premium_included;
    this.followed = tip.followed;
    this.betslip_image = tip.betslip_image;
    this.status = 'UNLOCKED';
  }
}

export interface IMediaFile {
  id: string;
  url: string;
  name: string;
  type: MediaFileType;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export type MediaFileType = 'VIDEO' | 'IMAGE' | 'TEXT' | 'AUDIO';
export interface UnlockedMatchForecast {
  id: string;
  market_code: string;
  market: string;
  pick: string;
  rate: number;
  result: TipResult;
  match_id: string;
  formatted_odds: FormattedOdds;
}

export interface BetslipImage {
  id: string;
  user_id: string;
  type: string;
  name: string;
  url: string;
  created_at: string;
  updated_at: string;
}

export interface TipInfoFromBE {
  tip: TipData;
  tipster: TipsterInfo;
  matches: MatchFromBE[];
}

export type TipsInfoState = {
  requested: boolean;
  tiersRequested: boolean;
  couponsRequested: boolean;
  isCouponsLoading: boolean;
  pricing: any;
  coupons: any;
  tipInfo: TipInfoFromBE | null;
  isLoading: boolean;
  error: string | null;
};
export type TipsInfoActions = ActionType<typeof tipsActions>;

export const TipsInfoReducer = combineReducers<TipsInfoState, TipsInfoActions>({
  requested: (state = false, action) => {
    switch (action.type) {
      case TIP_PRODUCT_REQUEST:
        return true;
      default:
        return state;
    }
  },
  tiersRequested: (state = false, action) => {
    switch (action.type) {
      case PRICING_TIERS_REQUEST:
        return true;
      default:
        return state;
    }
  },
  couponsRequested: (state = false, action) => {
    switch (action.type) {
      case COUPON_REQUEST:
        return true;
      default:
        return state;
    }
  },
  tipInfo: (state = null, action) => {
    switch (action.type) {
      case TIP_PRODUCT_SUCCESS:
        return action.payload;
      case TIP_PRODUCT_REQUEST:
        return null;
      default:
        return state;
    }
  },

  pricing: (state = [], action) => {
    switch (action.type) {
      case PRICING_TIERS_SUCCESS:
        return action.payload.pricing_tiers;
      default:
        return state;
    }
  },
  coupons: (state = [], action) => {
    switch (action.type) {
      case COUPON_SUCCESS:
        return action.payload.coupons;
      default:
        return state;
    }
  },
  isCouponsLoading: (state = false, action) => {
    switch (action.type) {
      case COUPON_REQUEST:
        return true;
      case COUPON_SUCCESS:
      case COUPON_FAILURE:
        return false;
      default:
        return state;
    }
  },
  isLoading: (state = false, action) => {
    switch (action.type) {
      case PRICING_TIERS_REQUEST:
      case TIP_PRODUCT_REQUEST:
        return true;
      case PRICING_TIERS_FAILURE:
      case PRICING_TIERS_SUCCESS:
      case TIP_PRODUCT_FAILURE:
      case TIP_PRODUCT_SUCCESS:
        return false;
      default:
        return state;
    }
  },
  error: (state = null, action) => {
    switch (action.type) {
      case PRICING_TIERS_FAILURE:
      case COUPON_FAILURE:
      case TIP_PRODUCT_FAILURE:
        return action.payload.message || action.payload.detail;
      default:
        return null;
    }
  },
});
